import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import CurrencyInput from "@/components/ui/currency-input";
import DateField from "@/components/ui/date-field";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { SubmitButton } from "@/components/ui/submit-button";
import { Switch } from "@/components/ui/switch";
import VehicleSelect from "@/components/ui/vehicle-select";
import { useFormSubmit } from "@/hooks/useFormSubmit";
import { errorHandler } from "@/lib/utils";
import { vehicleFeeService } from "@/services/api/fees";
import { zodResolver } from "@hookform/resolvers/zod";
import { ChevronLeft } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm, type Resolver } from "react-hook-form";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";

const formSchema = z
  .object({
    vehicle_id: z.number({
      message: "Veículo é obrigatório",
    }),
    name: z.string().min(1, "Nome é obrigatório"),
    amount: z.number().min(0.01, "Valor deve ser maior que zero"),
    due_date: z.string().min(1, "Data de vencimento é obrigatória"),
    payment_date: z.string().nullable(),
    is_recurring: z.boolean().default(false),
    recurrence_last_due_date: z.string().nullable(),
    notification_enabled: z.boolean().default(false),
    notification_30_days: z.boolean().default(false),
    notification_15_days: z.boolean().default(false),
  })
  .superRefine((values, ctx) => {
    if (
      values.is_recurring &&
      values.recurrence_last_due_date &&
      values.recurrence_last_due_date < values.due_date
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "A data do último vencimento deve ser igual ou posterior à data de vencimento.",
        path: ["recurrence_last_due_date"],
      });
    }
  });

type FormValues = z.infer<typeof formSchema>;

const FeeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(!!id);

  const form = useForm<FormValues>({
    // ponytail: resolvers 5 types the raw input apart from the parsed
    // output; the form keeps the parsed type, as it did on resolvers 3.
    resolver: zodResolver(formSchema) as Resolver<FormValues>,
    defaultValues: {
      vehicle_id: 0,
      name: "",
      amount: 0,
      due_date: "",
      payment_date: null,
      is_recurring: false,
      recurrence_last_due_date: null,
      notification_enabled: false,
      notification_30_days: false,
      notification_15_days: false,
    },
  });

  const isRecurring = form.watch("is_recurring");
  const notificationEnabled = form.watch("notification_enabled");

  useEffect(() => {
    const loadFee = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const response = await vehicleFeeService.show(Number(id));
        const amount = parseFloat(response.data.amount.toString());

        form.reset({
          vehicle_id: response.data.vehicle_id,
          name: response.data.name,
          amount,
          due_date: response.data.due_date,
          payment_date: response.data.payment_date,
          is_recurring: response.data.is_recurring ?? false,
          recurrence_last_due_date: response.data.recurrence_last_due_date,
          notification_enabled: false,
          notification_30_days: false,
          notification_15_days: false,
        });
      } catch (err) {
        errorHandler(err, "Erro ao carregar taxa");
      } finally {
        setIsLoading(false);
      }
    };

    loadFee();
  }, [id, form]);

  const { handleSubmit, loading } = useFormSubmit({
    id,
    createService: vehicleFeeService.store,
    updateService: vehicleFeeService.update,
    onSuccess: () => navigate("/fees"),
  });

  const onSubmit = async (values: FormValues) => {
    await handleSubmit({
      vehicle_id: values.vehicle_id,
      name: values.name,
      amount: values.amount,
      due_date: values.due_date,
      payment_date: values.payment_date,
      is_recurring: values.is_recurring,
      recurrence_last_due_date: values.is_recurring
        ? values.recurrence_last_due_date
        : null,
      notification_30_days:
        values.notification_enabled && values.notification_30_days ? 1 : 0,
      notification_15_days:
        values.notification_enabled && values.notification_15_days ? 1 : 0,
    });
  };

  if (isLoading) {
    return (
      <Card className="mx-auto">
        <CardHeader>
          <CardTitle>Editar Taxa</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <Skeleton className="h-10 w-full md:w-1/2" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-28 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mx-auto">
      <CardHeader>
        <CardTitle>{id ? "Editar Taxa" : "Nova Taxa"}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
              <FormField
                control={form.control}
                name="vehicle_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Veículo</FormLabel>
                    <VehicleSelect
                      value={field.value}
                      onChange={field.onChange}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nome</FormLabel>
                    <FormControl>
                      <Input placeholder="Ex: IPVA, Licenciamento" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="md:w-1/2">
              <FormField
                control={form.control}
                name="amount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valor</FormLabel>
                    <FormControl>
                      <CurrencyInput
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DateField
                name="due_date"
                form={form}
                label="Data de vencimento"
              />
              <DateField
                name="payment_date"
                form={form}
                label="Data de pagamento"
              />
            </div>

            <div className="rounded-lg border divide-y">
              <FormField
                control={form.control}
                name="notification_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Notificações</FormLabel>
                      <div className="text-sm text-muted-foreground">
                        Receber notificações na plataforma web/mobile perto da
                        data de vencimento
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="px-4 py-3 space-y-3">
                <p className="text-sm font-medium text-muted-foreground">
                  Antecedência
                </p>

                <FormField
                  control={form.control}
                  name="notification_30_days"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between">
                      <FormLabel
                        className={`text-sm font-normal ${!notificationEnabled ? "text-muted-foreground" : ""}`}
                      >
                        30 dias antes
                      </FormLabel>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!notificationEnabled}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="notification_15_days"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between">
                      <FormLabel
                        className={`text-sm font-normal ${!notificationEnabled ? "text-muted-foreground" : ""}`}
                      >
                        15 dias antes
                      </FormLabel>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={!notificationEnabled}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="rounded-lg border divide-y">
              <FormField
                control={form.control}
                name="is_recurring"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Recorrência</FormLabel>
                      <div className="text-sm text-muted-foreground">
                        Repetir esta taxa mensalmente com base no dia de
                        vencimento informado
                      </div>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              {isRecurring && (
                <div className="px-4 py-3">
                  <FormField
                    control={form.control}
                    name="recurrence_last_due_date"
                    render={() => (
                      <FormItem>
                        <DateField
                          name="recurrence_last_due_date"
                          form={form}
                          label="Data do último vencimento"
                        />
                        <FormDescription>
                          Deixe vazio para recorrência sem fim
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
            </div>

            <div className="flex space-x-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/fees")}
                className="flex items-center space-x-2"
              >
                <ChevronLeft className="w-4 h-4" />
                <span>Voltar</span>
              </Button>

              <SubmitButton loading={loading} />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default FeeForm;
